import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/hooks/useTheme';

interface StepBankAccountProps {
  data: Record<string, string>;
  onChange: (data: Record<string, string>) => void;
  errors: Record<string, string>;
}

export default function StepBankAccount({ data, onChange, errors }: StepBankAccountProps) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#8E8E93' : '#6B6B70';
  const inputBg = isDark ? '#1A1A1A' : '#fff';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';

  const fields = [
    { key: 'bankName', labelKey: 'ob_s4_bankName', phKey: 'ob_s4_bankName_ph', required: true },
    { key: 'iban', labelKey: 'ob_s4_iban', phKey: 'ob_s4_iban_ph', required: true, fullWidth: true },
    { key: 'currency', labelKey: 'ob_s4_currency', phKey: 'ob_s4_currency_ph', required: true },
    { key: 'mfo', labelKey: 'ob_s4_mfo', phKey: 'ob_s4_mfo_ph', required: true },
    { key: 'accountHolder', labelKey: 'ob_s4_accountHolder', phKey: 'ob_s4_accountHolder_ph', required: true, fullWidth: true },
  ];

  const handleChange = (key: string, value: string) => { onChange({ ...data, [key]: value }); };
  const handleBlur = (key: string) => { setTouched({ ...touched, [key]: true }); };

  return (
    <div className="space-y-5">
      <div className="mb-6">
        <h2 className="text-xl font-semibold" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>
          {t('ob_s4_title')}
        </h2>
        <p className="text-sm mt-1" style={{ color: textMuted }}>
          {t('ob_s4_subtitle')}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.map((field) => (
          <div key={field.key} className={field.fullWidth ? 'md:col-span-2' : ''}>
            <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>
              {t(field.labelKey)}
              {field.required && <span style={{ color: 'var(--accent)' }}>*</span>}
            </label>
            <input
              type="text"
              value={data[field.key] ?? ''}
              onChange={(e) => handleChange(field.key, e.target.value)}
              onBlur={() => handleBlur(field.key)}
              placeholder={t(field.phKey)}
              className="w-full px-3 py-2.5 rounded-md text-sm border transition-colors outline-none"
              style={{
                borderColor: touched[field.key] && errors[field.key] ? 'var(--accent)' : inputBorder,
                background: inputBg,
                color: textMain,
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
            />
            {touched[field.key] && errors[field.key] && (
              <p className="text-xs mt-1" style={{ color: 'var(--accent)' }}>{errors[field.key]}</p>
            )}
          </div>
        ))}
        <div className="md:col-span-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={data.isPrimary === 'true'}
              onChange={(e) => handleChange('isPrimary', e.target.checked ? 'true' : 'false')}
              className="w-4 h-4 rounded border cursor-pointer"
              style={{ accentColor: 'var(--accent)' }}
            />
            <span className="text-sm" style={{ color: textMain }}>
              {t('ob_s4_isPrimary')}
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}